import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
  Avatar,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  InputAdornment,
  Chip,
  Grid,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { getMe, updateMyProfile } from "../Services/profileService";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";

export default function ProfilePage() {
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", telephone: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialForm, setInitialForm] = useState({ nom: "", prenom: "", email: "", telephone: "" });
  const { showToast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    getMe()
      .then((me) => {
        if (mounted) {
          const formData = {
            nom: me.nom || "",
            prenom: me.prenom || "",
            email: me.email || "",
            telephone: me.telephone || "",
          };
          setForm(formData);
          setInitialForm(formData);
        }
      })
      .catch(() => showToast("Impossible de charger le profil", { severity: "error" }))
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, [showToast]);

  useEffect(() => {
    const changed =
      form.nom !== initialForm.nom ||
      form.prenom !== initialForm.prenom ||
      form.email !== initialForm.email ||
      form.telephone !== initialForm.telephone;
    setHasChanges(changed);
  }, [form, initialForm]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateMyProfile(form);
      setInitialForm(form);
      setHasChanges(false);
      showToast("Profil mis à jour avec succès", { severity: "success" });
    } catch (e) {
      showToast("Échec de la mise à jour", { severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const getInitials = () => {
    const first = form.prenom?.charAt(0)?.toUpperCase() || "";
    const last = form.nom?.charAt(0)?.toUpperCase() || "";
    return first + last || "U";
  };

  const getFullName = () => {
    return `${form.prenom || ""} ${form.nom || ""}`.trim() || "Utilisateur";
  };

  const primaryRole = () => {
    const roles = Array.isArray(user?.roles) ? user.roles : [];
    const main = roles.find((r) => r !== "ROLE_USER") || roles[0] || "";
    return main.replace("ROLE_", "") || "";
  };

  if (loading) {
    return (
      <div style={{ display: "flex" }}>
        <Sidebar />
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div style={{ display: "flex" }}>
      <Sidebar />
      <Box sx={{ flex: 1, bgcolor: "background.default", minHeight: "100vh" }}>
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1200, mx: "auto" }}>
          {/* En-tête avec avatar */}
          <Card sx={{ mb: 3, background: "linear-gradient(135deg, #ea5b0c 0%, #ff7a3d 100%)", color: "white" }}>
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="center">
                <Avatar
                  sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    fontSize: { xs: "2rem", sm: "2.5rem" },
                    fontWeight: "bold",
                    border: "3px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  {getInitials()}
                </Avatar>
                <Box sx={{ flex: 1, textAlign: { xs: "center", sm: "left" } }}>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {getFullName()}
                  </Typography>
                  <Stack direction="row" spacing={1} justifyContent={{ xs: "center", sm: "flex-start" }} flexWrap="wrap">
                    <Chip
                      label={primaryRole() || "Utilisateur"}
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        color: "white",
                        fontWeight: "bold",
                      }}
                      size="small"
                    />
                    {form.email && (
                      <Chip
                        label={form.email}
                        icon={<EmailIcon sx={{ color: "white !important" }} />}
                        sx={{
                          bgcolor: "rgba(255, 255, 255, 0.2)",
                          color: "white",
                        }}
                        size="small"
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Formulaire de modification */}
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                <EditIcon color="primary" />
                <Typography variant="h5" fontWeight="600">
                  Informations personnelles
                </Typography>
              </Stack>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Prénom"
                    name="prenom"
                    value={form.prenom}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nom"
                    name="nom"
                    value={form.nom}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Téléphone"
                    name="telephone"
                    value={form.telephone}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setForm(initialForm);
                    setHasChanges(false);
                  }}
                  disabled={!hasChanges || saving}
                >
                  Annuler
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  sx={{
                    minWidth: 140,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 4,
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  {saving ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </div>
  );
} 